import { EmailCampaignStats } from '@spike/email-marketing-model';
import {
    FullWindowModal,
    FullWindowModalBody,
    FullWindowModalCloseButton,
    FullWindowModalHeader
} from 'components/UI/V3/Modals/FullWindowModal';
import moment from 'moment';
import { FunctionComponent } from 'react';

import styles from './ViewEmailModal.module.scss';

interface ViewEmailModalProps {
    stats: EmailCampaignStats;
    onClose: () => void;
}

/**
 * Full-window preview of what actually went out — 2-column layout with a
 * metadata sidebar (delivery info, sender, subject, preview text, recipients)
 * and the HTML preview rendered in an iframe against the frozen template
 * snapshot.
 *
 * The iframe uses `sandbox=""` (empty allowlist) so the preview can never
 * execute scripts, submit forms, or navigate the parent — critical because
 * we're rendering arbitrary HTML authored via Unlayer.
 *
 * Merge tags in the preview are shown as literal placeholders (e.g.
 * `{{first_name}}`) because the snapshot HTML is captured pre-personalisation.
 * That matches the CUD-4208 AC "Email preview renders from HTML without PII".
 */
const ViewEmailModal: FunctionComponent<ViewEmailModalProps> = ({ stats, onClose }) => {
    const { campaign } = stats;
    const html = campaign.emailTemplate?.html;

    const formattedSentAt = formatSentAt(stats.sentAt);

    return (
        <FullWindowModal>
            <FullWindowModalHeader>
                <div className={styles.headerLayout}>
                    <img
                        className={styles.logo}
                        src="/images/logo/cuddles-logo.svg"
                        alt="Cuddles"
                    />
                    <h2 className={styles.headerTitle}>View Email</h2>
                    <FullWindowModalCloseButton
                        onClick={onClose}
                        ariaLabel="Close view email"
                        className={styles.closeButton}
                    />
                </div>
            </FullWindowModalHeader>

            <FullWindowModalBody className={styles.body}>
                <aside className={styles.metadata}>
                    <h3 className={styles.campaignName}>{campaign.name}</h3>
                    <dl className={styles.metadataList}>
                        <MetaRow
                            label="Delivery Date & Time"
                            value={formattedSentAt || '—'}
                        />
                        <MetaRow
                            label="From Name"
                            value={campaign.fromName || '—'}
                        />
                        <MetaRow
                            label="From Email"
                            value={campaign.fromEmail || '—'}
                        />
                        <MetaRow
                            label="Subject Line"
                            value={campaign.subject || '—'}
                        />
                        <MetaRow
                            label="Preview Text"
                            value={campaign.previewText || '—'}
                        />
                        <MetaRow
                            label="Recipients"
                            value={`All Clients · ${stats.recipientsCount.toLocaleString()} Recipients`}
                        />
                    </dl>
                </aside>

                <div className={styles.previewPane}>
                    {html ? (
                        <iframe
                            className={styles.iframe}
                            title={`${campaign.name} — preview`}
                            srcDoc={html}
                            sandbox=""
                        />
                    ) : (
                        <div className={styles.placeholder}>Email preview unavailable.</div>
                    )}
                </div>
            </FullWindowModalBody>
        </FullWindowModal>
    );
};

// ---------------------------------------------------------------------------
// Internal building blocks
// ---------------------------------------------------------------------------

interface MetaRowProps {
    label: string;
    value: string;
}

const MetaRow: FunctionComponent<MetaRowProps> = ({ label, value }) => (
    <div className={styles.metadataRow}>
        <dt className={styles.metadataLabel}>{label}</dt>
        <dd className={styles.metadataValue}>{value}</dd>
    </div>
);

const formatSentAt = (iso: string | null): string => {
    if (!iso) return '';
    const m = moment(iso);
    return m.isValid() ? m.format('MMM D, YYYY h:mm a') : '';
};

export default ViewEmailModal;
